import React from 'react';
import { FileWarning } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import type { TimesheetDeadlineSettings } from '@/types/timesheetSettings';

interface MissingTimesheetReminderSettingsProps {
  settings: TimesheetDeadlineSettings;
  onChange: (settings: TimesheetDeadlineSettings, isValid: boolean) => void;
}

// ---------------------------------------------------------------------------
// Setting card — matches Submission Reminders card pattern
// ---------------------------------------------------------------------------

interface SettingCardProps {
  id: string;
  label: string;
  description: string;
  control: React.ReactNode;
  onClick?: () => void;
}

function SettingCard({ id, label, description, control, onClick }: SettingCardProps) {
  return (
    <div
      className={`flex items-center justify-between gap-4 p-4 rounded-lg bg-background transition-colors
        ${onClick ? 'cursor-pointer select-none hover:bg-accent/50' : ''}
      `}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className={`text-sm font-medium leading-snug ${onClick ? 'cursor-pointer select-none' : ''}`}
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        {control}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MissingTimesheetReminderSettings: React.FC<MissingTimesheetReminderSettingsProps> = ({
  settings,
  onChange,
}) => {
  const handleSendFeedRemindersChange = (checked: boolean) => {
    const newSettings = {
      ...settings,
      sendFeedReminders: checked,
    };
    onChange(newSettings, true);
  };

  return (
    <Accordion type="multiple" defaultValue={['missing-timesheet-reminders']} className="space-y-4">
      <AccordionItem value="missing-timesheet-reminders" className="border rounded-lg">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <FileWarning className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold">Missing Timesheet Reminders</p>
              <p className="text-xs text-muted-foreground font-normal mt-0.5">
                Configure reminders for missing timesheets
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pt-2 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <SettingCard
              id="sendFeedReminders"
              label="Missing Timesheet Report Reminders"
              description="Allow sending reminders for missing timesheets from the Missing Timesheet Report"
              control={
                <Checkbox
                  id="sendFeedReminders"
                  checked={settings.sendFeedReminders}
                  onCheckedChange={(v) => handleSendFeedRemindersChange(!!v)}
                />
              }
              onClick={() => handleSendFeedRemindersChange(!settings.sendFeedReminders)}
            />
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
